import React, { useState } from 'react';
import { questionnairesAPI } from '../../utils/api';
import toastr from 'toastr';

const QUESTION_TYPES = [
  { value: 'text', label: 'Short Text' },
  { value: 'textarea', label: 'Long Text' },
  { value: 'multiple_choice', label: 'Multiple Choice' },
  { value: 'yes_no', label: 'Yes/No' },
  { value: 'scale', label: 'Scale (1-10)' },
  { value: 'checkbox', label: 'Checkbox (Select All)' },
  { value: 'file_upload', label: 'File Upload' }
];

const QuestionnaireBuilder = ({ jobPostingId, onClose, onSave, existingQuestionnaire = null }) => {
  const [title, setTitle] = useState(existingQuestionnaire?.title || '');
  const [description, setDescription] = useState(existingQuestionnaire?.description || '');
  const [questions, setQuestions] = useState(existingQuestionnaire?.questions || []);
  const [deadline, setDeadline] = useState(existingQuestionnaire?.deadline || 48);
  const [allowResubmission, setAllowResubmission] = useState(existingQuestionnaire?.allowResubmission || false);
  const [status, setStatus] = useState(existingQuestionnaire?.status || 'draft');
  const [saving, setSaving] = useState(false);

  const addQuestion = () => {
    setQuestions([...questions, {
      question: '',
      type: 'text',
      required: false,
      options: [],
      scaleMin: 1,
      scaleMax: 10,
      autoScore: false,
      order: questions.length
    }]);
  };

  const removeQuestion = (index) => {
    setQuestions(questions.filter((_, i) => i !== index).map((q, i) => ({ ...q, order: i })));
  };

  const updateQuestion = (index, field, value) => {
    const updated = [...questions];
    updated[index] = { ...updated[index], [field]: value };
    setQuestions(updated);
  };

  const addOption = (questionIndex) => {
    const updated = [...questions];
    if (!updated[questionIndex].options) {
      updated[questionIndex].options = [];
    }
    updated[questionIndex].options.push({ label: '', value: '', score: 0 });
    setQuestions(updated);
  };

  const removeOption = (questionIndex, optionIndex) => {
    const updated = [...questions];
    updated[questionIndex].options = updated[questionIndex].options.filter((_, i) => i !== optionIndex);
    setQuestions(updated);
  };

  const updateOption = (questionIndex, optionIndex, field, value) => {
    const updated = [...questions];
    updated[questionIndex].options[optionIndex] = {
      ...updated[questionIndex].options[optionIndex],
      [field]: value
    };
    setQuestions(updated);
  };

  const moveQuestion = (index, direction) => {
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === questions.length - 1)) {
      return;
    }
    const updated = [...questions];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    updated.forEach((q, i) => { q.order = i; });
    setQuestions(updated);
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toastr.error('Please enter a questionnaire title');
      return;
    }

    if (questions.length === 0) {
      toastr.error('Please add at least one question');
      return;
    }

    // Validate questions
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      if (!q.question.trim()) {
        toastr.error(`Question ${i + 1} is missing text`);
        return;
      }
      if ((q.type === 'multiple_choice' || q.type === 'checkbox') && (!q.options || q.options.length === 0)) {
        toastr.error(`Question ${i + 1} (${q.type}) requires at least one option`);
        return;
      }
      if (q.type === 'multiple_choice' || q.type === 'checkbox') {
        for (let j = 0; j < q.options.length; j++) {
          if (!q.options[j].label.trim() || !q.options[j].value.trim()) {
            toastr.error(`Question ${i + 1}, Option ${j + 1} is incomplete`);
            return;
          }
        }
      }
    }

    setSaving(true);
    try {
      const questionnaireData = {
        jobPostingId,
        title: title.trim(),
        description: description.trim(),
        questions: questions.map((q, i) => ({
          ...q,
          question: q.question.trim(),
          order: i
        })),
        deadline,
        allowResubmission,
        status
      };

      let result;
      if (existingQuestionnaire) {
        result = await questionnairesAPI.update(existingQuestionnaire._id, questionnaireData);
      } else {
        result = await questionnairesAPI.create(questionnaireData);
      }

      toastr.success(existingQuestionnaire ? 'Questionnaire updated successfully' : 'Questionnaire created successfully');
      if (onSave) {
        onSave(result.data.questionnaire);
      }
      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error('Failed to save questionnaire:', error);
      toastr.error(error.response?.data?.error || 'Failed to save questionnaire');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      background: '#ffffff',
      borderRadius: '8px',
      padding: '2rem',
      maxWidth: '900px',
      maxHeight: '90vh',
      overflowY: 'auto',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
    }}>
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ margin: '0 0 1rem 0', fontSize: '1.5rem', fontWeight: '600', color: '#1F2937' }}>
          {existingQuestionnaire ? 'Edit Questionnaire' : 'Create Pre-Interview Questionnaire'}
        </h2>
        <p style={{ margin: 0, color: '#6B7280', fontSize: '0.875rem' }}>
          Build a custom questionnaire to screen candidates before scheduling interviews
        </p>
      </div>

      {/* Basic Information */}
      <div style={{ marginBottom: '2rem' }}>
        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
          Questionnaire Title <span style={{ color: '#EF4444' }}>*</span>
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g., Technical Screening Questions"
          style={{
            width: '100%',
            padding: '0.625rem 0.875rem',
            border: '1px solid #D1D5DB',
            borderRadius: '6px',
            fontSize: '0.875rem'
          }}
        />
      </div>

      <div style={{ marginBottom: '2rem' }}>
        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
          Description (Optional)
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Brief description of what this questionnaire covers..."
          rows={3}
          style={{
            width: '100%',
            padding: '0.625rem 0.875rem',
            border: '1px solid #D1D5DB',
            borderRadius: '6px',
            fontSize: '0.875rem',
            fontFamily: 'inherit',
            resize: 'vertical'
          }}
        />
      </div>

      {/* Settings */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: '1fr 1fr', 
        gap: '1rem',
        marginBottom: '2rem',
        padding: '1rem',
        background: '#F9FAFB',
        borderRadius: '6px'
      }}>
        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151', fontSize: '0.875rem' }}>
            Deadline (hours)
          </label>
          <input
            type="number"
            value={deadline}
            onChange={(e) => setDeadline(parseInt(e.target.value) || 48)}
            min={1}
            max={168}
            style={{
              width: '100%',
              padding: '0.625rem 0.875rem',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '0.875rem'
            }}
          />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151', fontSize: '0.875rem' }}>
            Status
          </label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            style={{
              width: '100%',
              padding: '0.625rem 0.875rem',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '0.875rem',
              background: '#ffffff'
            }}
          >
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="archived">Archived</option>
          </select>
        </div>
      </div>

      <div style={{ marginBottom: '2rem' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={allowResubmission}
            onChange={(e) => setAllowResubmission(e.target.checked)}
            style={{ cursor: 'pointer' }}
          />
          <span style={{ fontSize: '0.875rem', color: '#374151' }}>
            Allow candidates to resubmit if they already received this questionnaire
          </span>
        </label>
      </div>

      {/* Questions */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '600', color: '#1F2937' }}>
            Questions ({questions.length})
          </h3>
          <button
            onClick={addQuestion}
            style={{
              padding: '0.5rem 1rem',
              background: '#1E40AF',
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '500',
              fontSize: '0.875rem'
            }}
            onMouseEnter={(e) => e.target.style.background = '#1E3A8A'}
            onMouseLeave={(e) => e.target.style.background = '#1E40AF'}
          >
            + Add Question
          </button>
        </div>

        {questions.length === 0 ? (
          <div style={{
            padding: '2rem',
            textAlign: 'center',
            background: '#F9FAFB',
            borderRadius: '6px',
            color: '#6B7280'
          }}>
            No questions yet. Click "Add Question" to get started.
          </div>
        ) : (
          questions.map((question, index) => (
            <div key={index} style={{
              marginBottom: '1.5rem',
              padding: '1.5rem',
              border: '1px solid #E5E7EB',
              borderRadius: '6px',
              background: '#ffffff'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                    <input
                      type="text"
                      value={question.question}
                      onChange={(e) => updateQuestion(index, 'question', e.target.value)}
                      placeholder="Enter your question..."
                      style={{
                        flex: 1,
                        padding: '0.625rem 0.875rem',
                        border: '1px solid #D1D5DB',
                        borderRadius: '6px',
                        fontSize: '0.875rem'
                      }}
                    />
                    <select
                      value={question.type}
                      onChange={(e) => updateQuestion(index, 'type', e.target.value)}
                      style={{
                        padding: '0.625rem 0.875rem',
                        border: '1px solid #D1D5DB',
                        borderRadius: '6px',
                        fontSize: '0.875rem',
                        background: '#ffffff',
                        minWidth: '180px'
                      }}
                    >
                      {QUESTION_TYPES.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Question-specific fields */}
                  {(question.type === 'multiple_choice' || question.type === 'checkbox') && (
                    <div style={{ marginTop: '0.75rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                        <label style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>Options</label>
                        <button
                          onClick={() => addOption(index)}
                          style={{
                            padding: '0.25rem 0.75rem',
                            background: '#F3F4F6',
                            color: '#374151',
                            border: '1px solid #D1D5DB',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.75rem'
                          }}
                          onMouseEnter={(e) => e.target.style.background = '#E5E7EB'}
                          onMouseLeave={(e) => e.target.style.background = '#F3F4F6'}
                        >
                          + Add Option
                        </button>
                      </div>
                      {(question.options || []).map((option, optIndex) => (
                        <div key={optIndex} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                          <input
                            type="text"
                            value={option.label}
                            onChange={(e) => updateOption(index, optIndex, 'label', e.target.value)}
                            placeholder="Option label"
                            style={{
                              flex: 1,
                              padding: '0.5rem 0.75rem',
                              border: '1px solid #D1D5DB',
                              borderRadius: '4px',
                              fontSize: '0.875rem'
                            }}
                          />
                          <input
                            type="text"
                            value={option.value}
                            onChange={(e) => updateOption(index, optIndex, 'value', e.target.value)}
                            placeholder="Value"
                            style={{
                              width: '120px',
                              padding: '0.5rem 0.75rem',
                              border: '1px solid #D1D5DB',
                              borderRadius: '4px',
                              fontSize: '0.875rem'
                            }}
                          />
                          {question.autoScore && (
                            <input
                              type="number"
                              value={option.score || 0}
                              onChange={(e) => updateOption(index, optIndex, 'score', parseInt(e.target.value) || 0)}
                              placeholder="Score"
                              style={{
                                width: '80px',
                                padding: '0.5rem 0.75rem',
                                border: '1px solid #D1D5DB',
                                borderRadius: '4px',
                                fontSize: '0.875rem'
                              }}
                            />
                          )}
                          <button
                            onClick={() => removeOption(index, optIndex)}
                            style={{
                              padding: '0.5rem 0.75rem',
                              background: '#EF4444',
                              color: '#ffffff',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '0.875rem'
                            }}
                            onMouseEnter={(e) => e.target.style.background = '#DC2626'}
                            onMouseLeave={(e) => e.target.style.background = '#EF4444'}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {question.type === 'scale' && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginTop: '0.75rem' }}>
                      <div>
                        <label style={{ fontSize: '0.75rem', color: '#6B7280' }}>Min</label>
                        <input
                          type="number"
                          value={question.scaleMin || 1}
                          onChange={(e) => updateQuestion(index, 'scaleMin', parseInt(e.target.value) || 1)}
                          style={{
                            width: '100%',
                            padding: '0.5rem 0.75rem',
                            border: '1px solid #D1D5DB',
                            borderRadius: '4px',
                            fontSize: '0.875rem'
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: '0.75rem', color: '#6B7280' }}>Max</label>
                        <input
                          type="number"
                          value={question.scaleMax || 10}
                          onChange={(e) => updateQuestion(index, 'scaleMax', parseInt(e.target.value) || 10)}
                          style={{
                            width: '100%',
                            padding: '0.5rem 0.75rem',
                            border: '1px solid #D1D5DB',
                            borderRadius: '4px',
                            fontSize: '0.875rem'
                          }}
                        />
                      </div>
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '1rem', marginTop: '0.75rem', alignItems: 'center' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={question.required || false}
                        onChange={(e) => updateQuestion(index, 'required', e.target.checked)}
                        style={{ cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '0.875rem', color: '#374151' }}>Required</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={question.autoScore || false}
                        onChange={(e) => updateQuestion(index, 'autoScore', e.target.checked)}
                        style={{ cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '0.875rem', color: '#374151' }}>Auto-score</span>
                    </label>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', marginLeft: '0.5rem' }}>
                  {index > 0 && (
                    <button
                      onClick={() => moveQuestion(index, 'up')}
                      style={{
                        padding: '0.375rem 0.5rem',
                        background: '#F3F4F6',
                        color: '#374151',
                        border: '1px solid #D1D5DB',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.75rem'
                      }}
                      onMouseEnter={(e) => e.target.style.background = '#E5E7EB'}
                      onMouseLeave={(e) => e.target.style.background = '#F3F4F6'}
                    >
                      ↑
                    </button>
                  )}
                  {index < questions.length - 1 && (
                    <button
                      onClick={() => moveQuestion(index, 'down')}
                      style={{
                        padding: '0.375rem 0.5rem',
                        background: '#F3F4F6',
                        color: '#374151',
                        border: '1px solid #D1D5DB',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.75rem'
                      }}
                      onMouseEnter={(e) => e.target.style.background = '#E5E7EB'}
                      onMouseLeave={(e) => e.target.style.background = '#F3F4F6'}
                    >
                      ↓
                    </button>
                  )}
                  <button
                    onClick={() => removeQuestion(index)}
                    style={{
                      padding: '0.375rem 0.5rem',
                      background: '#EF4444',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.75rem'
                    }}
                    onMouseEnter={(e) => e.target.style.background = '#DC2626'}
                    onMouseLeave={(e) => e.target.style.background = '#EF4444'}
                  >
                    ×
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', paddingTop: '1rem', borderTop: '1px solid #E5E7EB' }}>
        <button
          onClick={onClose}
          disabled={saving}
          style={{
            padding: '0.625rem 1.5rem',
            background: '#F3F4F6',
            color: '#374151',
            border: '1px solid #D1D5DB',
            borderRadius: '6px',
            cursor: saving ? 'not-allowed' : 'pointer',
            fontWeight: '500',
            fontSize: '0.875rem'
          }}
          onMouseEnter={(e) => !saving && (e.target.style.background = '#E5E7EB')}
          onMouseLeave={(e) => !saving && (e.target.style.background = '#F3F4F6')}
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: '0.625rem 1.5rem',
            background: saving ? '#9CA3AF' : '#1E40AF',
            color: '#ffffff',
            border: 'none',
            borderRadius: '6px',
            cursor: saving ? 'not-allowed' : 'pointer',
            fontWeight: '500',
            fontSize: '0.875rem'
          }}
          onMouseEnter={(e) => !saving && (e.target.style.background = saving ? '#9CA3AF' : '#1E3A8A')}
          onMouseLeave={(e) => !saving && (e.target.style.background = saving ? '#9CA3AF' : '#1E40AF')}
        >
          {saving ? 'Saving...' : existingQuestionnaire ? 'Update Questionnaire' : 'Create Questionnaire'}
        </button>
      </div>
    </div>
  );
};

export default QuestionnaireBuilder;


